import React, { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "../lib/providers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!token) { setError(lang === "ar" ? "رابط غير صالح" : "Lien invalide"); return; }
    if (pwd.length < 8) { setError(lang === "ar" ? "8 أحرف على الأقل" : "Mot de passe : 8 caractères minimum"); return; }
    if (pwd !== pwd2) { setError(lang === "ar" ? "كلمتا المرور غير متطابقتين" : "Les mots de passe ne correspondent pas"); return; }
    setState("loading");
    try {
      const { data } = await axios.post(`${API}/customer/reset-password`, { token, new_password: pwd });
      if (data?.ok) {
        setState("success");
        setTimeout(() => navigate("/mon-compte"), 2500);
      } else {
        setError(
          data?.reason === "expired"
            ? (lang === "ar" ? "انتهت صلاحية الرابط" : "Lien expiré (60 min)")
            : data?.reason === "weak_password"
            ? (lang === "ar" ? "كلمة مرور ضعيفة" : "Mot de passe trop court")
            : (lang === "ar" ? "رابط غير صالح" : "Lien invalide")
        );
        setState("idle");
      }
    } catch {
      setError(lang === "ar" ? "خطأ، حاول مرة أخرى" : "Erreur, réessayez");
      setState("idle");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-black">
      <div className="max-w-md w-full border border-white/10 p-8 bg-black/60" dir={lang === "ar" ? "rtl" : "ltr"}>
        {state === "success" ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 className="w-14 h-14 text-neon mx-auto mb-4" />
            <h1 className="text-2xl font-black uppercase tracking-tight mb-2">
              {lang === "ar" ? "✅ تم تحديث كلمة المرور" : "✅ Mot de passe mis à jour"}
            </h1>
            <p className="text-sm text-white/70">{lang === "ar" ? "تم تسجيل الخروج من كل الجلسات الأخرى. إعادة التوجيه..." : "Toutes les autres sessions ont été déconnectées. Redirection..."}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <h1 className="text-xl font-black uppercase tracking-tight mb-2">
              {lang === "ar" ? "كلمة مرور جديدة" : "Nouveau mot de passe"}
            </h1>
            <input
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder={lang === "ar" ? "كلمة المرور (8+ أحرف)" : "Mot de passe (8+ caractères)"}
              className="w-full bg-black/60 border border-white/15 px-3 py-3 text-sm focus:border-neon outline-none"
              data-testid="reset-pwd1"
              autoFocus
            />
            <input
              type="password"
              value={pwd2}
              onChange={(e) => setPwd2(e.target.value)}
              placeholder={lang === "ar" ? "تأكيد كلمة المرور" : "Confirmer le mot de passe"}
              className="w-full bg-black/60 border border-white/15 px-3 py-3 text-sm focus:border-neon outline-none"
              data-testid="reset-pwd2"
            />
            {error && <p className="text-xs text-clay" data-testid="reset-error">{error}</p>}
            <button
              type="submit"
              disabled={state === "loading"}
              className="w-full bg-neon text-black hover:opacity-90 px-6 py-3 font-black uppercase tracking-widest text-sm disabled:opacity-50"
              data-testid="reset-submit"
            >
              {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin inline-block" /> : (lang === "ar" ? "تحديث" : "Mettre à jour")}
            </button>
            <Link to="/" className="block text-center text-xs text-white/50 hover:text-neon underline">
              {lang === "ar" ? "العودة إلى المتجر" : "Retour à la boutique"}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
